import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import Button from '@roo/roo/Button';

import { RootState } from '@projects/Shell/store';
import * as LX from '@utils/lxWrapper';
import isSafeUrl from '@utils/isSafeUrl';
import { getMessageConfig } from '@utils/messageConfig';
import { isHeadMultiPoi, getWmPoiId } from '@utils/ids';
import request from '@utils/axiosWrapper';
import { successToast, failToast } from '@utils/toast';

import { MessageNotification } from '../../store/models/pushNotification';
import * as pushMessageReceipt from '../../connection/push/pushMessageReceipt';
import { CustomizedShowArrType } from '../../api/define';

import styles from './StrongNotificationBox.module.scss';

const { useCallback, useState, useEffect } = React;

const getButtonText = (notification: MessageNotification) => {
    return notification.href ? '查看详情' : '我知道了';
};

type StrongNotificationBoxProps = {
    notifications: MessageNotification[];
    foldNotificationCount: number;
    clearNotification: () => void;
    shouldShowNotification: boolean;
    closeNotification: (notification: MessageNotification) => void;
};

type ClickInfo = {
    clickButtonText?: string;
    utmContent?: string;
    clickButtonType?: 'CONFIRM' | 'CONVERT' | 'CANCEL';
};

const StrongNotificationBox: React.FC<StrongNotificationBoxProps> = ({
    notifications,
    foldNotificationCount,
    clearNotification,
    shouldShowNotification,
    closeNotification,
}) => {
    /** 是否展示重要消息通知，可通过白名单或者消息提醒设置页配置 */
    const [messageConfig, setMessageConfig] = useState(true);

    /** 获取重要消息配置 */
    useEffect(() => {
        setMessageConfig(getMessageConfig().strongNotification !== false);
    }, []);

    const handleCloseNotification = useCallback(
        (notification: MessageNotification, clickInfo: ClickInfo = {}) => {
            closeNotification(notification);

            if (notification.noticeId) {
                LX.reportMC('b_waimai_e_qtjkyat8_mc', 'c_waimai_e_fa4ps4ng', {
                    // eslint-disable-next-line @typescript-eslint/camelcase
                    wmb_pic_msg_id: notification.noticeId,
                });
            }

            pushMessageReceipt.reportUnifiedBusinessMessageNoticeRead(notification, {
                clickButtonText: clickInfo.clickButtonText || '',
                clickButtonType: clickInfo.clickButtonType || 'CANCEL',
                utmContent: clickInfo.utmContent,
            });
        },
        [closeNotification],
    );

    const handleReadNotification = useCallback(
        (notification: MessageNotification, clickInfo: ClickInfo = {}) => {
            closeNotification(notification);

            if (notification.noticeId) {
                LX.reportMC('b_waimai_e_j3r8xrb1_mc', 'c_waimai_e_fa4ps4ng', {
                    // eslint-disable-next-line @typescript-eslint/camelcase
                    wmb_pic_msg_id: notification.noticeId,
                });
            }

            pushMessageReceipt.reportUnifiedBusinessMessageNoticeRead(notification, {
                clickButtonText: clickInfo.clickButtonText || getButtonText(notification),
                clickButtonType: clickInfo.clickButtonType || 'CONFIRM',
                utmContent: clickInfo.utmContent,
            });
            // 渠道触达统一数据监控
            LX.reportMC('b_waimai_e_a3egn8lk_mc', 'c_waimai_e_742h21bc', {
                poi_id: notification.wmPoiId || getWmPoiId(),
                msgId: notification.wmEPoiMsgId,
                traceId: notification.wmEMsgTraceId,
                pushType: 2,
            });

            if (notification.href) {
                const href = notification.href;

                if (href.indexOf('http') === 0) {
                    if (isSafeUrl(href)) {
                        window.location.hash = href;
                    } else {
                        window.open(href);
                    }
                } else {
                    // 相对路径，修改hash
                    window.location.hash = href;
                }
            }
        },
        [closeNotification],
    );

    // 是否无条件屏蔽所有强提醒消息（XBK、连锁商家全部门店下手动选择管理消息提醒）
    const hideAllStrongNotification =
        !shouldShowNotification || (isHeadMultiPoi() && !messageConfig);

    useEffect(() => {
        if (!hideAllStrongNotification) {
            notifications.forEach(n => {
                // 渠道触达统一数据监控
                LX.reportMV('b_waimai_e_a3egn8lk_mv', 'c_waimai_e_742h21bc', {
                    poi_id: n.wmPoiId || getWmPoiId(),
                    msgId: n.wmEPoiMsgId,
                    traceId: n.wmEMsgTraceId,
                    pushType: 2,
                });
            });
        }
    }, [hideAllStrongNotification, notifications.map(n => n.wmEMsgTraceId).join('-')]);

    /** 渲染定制消息操作区域 */
    const renderCustomizeMessageOpe = useCallback(
        (message: MessageNotification) => {
            if (message.dialogType !== 2 || !message.customizeDialogConfig) {
                return null;
            }

            let ops: React.ReactNode[] = [];

            try {
                ops = [
                    ...(message.customizeDialogConfig.buttonCnt === 1
                        ? []
                        : [
                              {
                                  btnType: message.customizeDialogConfig.leftBtnType,
                                  ...message.customizeDialogConfig.leftBtnConfig,
                                  utmContent: message.customizeDialogConfig.utmContent,
                              },
                          ]),
                    {
                        btnType: message.customizeDialogConfig.rightBtnType,
                        ...message.customizeDialogConfig.rightBtnConfig,
                        utmContent: message.customizeDialogConfig.utmContent,
                    },
                ].map((config, index) => {
                    return (
                        <Button
                            size="mini"
                            type={
                                index === 0 &&
                                message.customizeDialogConfig &&
                                message.customizeDialogConfig.buttonCnt !== 1
                                    ? 'hollow'
                                    : undefined
                            }
                            onClick={() => {
                                if (config.type === 2) {
                                    // 请求接口
                                    // eslint-disable-next-line no-new
                                    new Promise<{ msg: string; url?: string }>(
                                        (resolve, reject) => {
                                            if (!config.pcUrl && !config.url) {
                                                resolve({ msg: '' });
                                            } else {
                                                request
                                                    .rawPost<{
                                                        code: number;
                                                        msg: string;
                                                        data?: { url?: string };
                                                    }>((config.pcUrl || config.url)!)
                                                    .then(
                                                        response => {
                                                            if (response.code === 0) {
                                                                resolve({
                                                                    msg:
                                                                        config.succToast ||
                                                                        response.msg ||
                                                                        '',
                                                                    url:
                                                                        (response.data &&
                                                                            response.data.url) ||
                                                                        '',
                                                                });
                                                            } else {
                                                                // eslint-disable-next-line prefer-promise-reject-errors
                                                                reject({
                                                                    msg:
                                                                        config.failToast ||
                                                                        response.msg ||
                                                                        '',
                                                                });
                                                            }
                                                        },
                                                        error => {
                                                            // 网络请求失败
                                                            // eslint-disable-next-line prefer-promise-reject-errors
                                                            reject({
                                                                msg:
                                                                    config.failToast ||
                                                                    error.message ||
                                                                    '',
                                                            });
                                                        },
                                                    );
                                            }
                                        },
                                    ).then(
                                        result => {
                                            if (result.msg) {
                                                successToast(result.msg);
                                            }

                                            if (result.url) {
                                                const href = result.url;
                                                if (href.indexOf('http') === 0) {
                                                    if (isSafeUrl(href)) {
                                                        window.location.hash = href;
                                                    } else {
                                                        window.open(href);
                                                    }
                                                } else {
                                                    // 相对路径，修改hash
                                                    window.location.hash = href;
                                                }
                                            }

                                            // 消息已读
                                            handleReadNotification(message, {
                                                clickButtonText: config.buttonText,
                                                utmContent: config.utmContent,
                                                clickButtonType: config.btnType,
                                            });
                                        },
                                        result => {
                                            if (result.msg) {
                                                failToast(result.msg);
                                            }
                                            closeNotification(message);
                                        },
                                    );
                                } else if (config.type === 3) {
                                    const href = config.pcUrl ? config.pcUrl : config.url;
                                    if (href) {
                                        if (href.indexOf('http') === 0) {
                                            if (isSafeUrl(href)) {
                                                window.location.hash = href;
                                            } else {
                                                window.open(href);
                                            }
                                        } else {
                                            // 相对路径，修改hash
                                            window.location.hash = href;
                                        }
                                    }
                                    // 直接关闭消息
                                    handleCloseNotification(message, {
                                        clickButtonText: config.buttonText,
                                        utmContent: config.utmContent,
                                        clickButtonType: config.btnType,
                                    });
                                } else {
                                    // 直接关闭消息
                                    handleCloseNotification(message, {
                                        clickButtonText: config.buttonText,
                                        utmContent: config.utmContent,
                                        clickButtonType: config.btnType,
                                    });
                                }
                            }}
                        >
                            {config.buttonText}
                        </Button>
                    );
                });
            } catch (e) {
                // be quiet
            }

            return <>{ops}</>;
        },
        [handleReadNotification, handleCloseNotification],
    );

    if (notifications.length === 0 || hideAllStrongNotification) {
        return null;
    }

    return (
        <div className={styles.backdrop}>
            <div className={styles.container} onClick={e => e.stopPropagation()}>
                <div>
                    {notifications.map(notification => (
                        <div className={styles.msg} key={notification.wmEMsgTraceId}>
                            <div className={styles.msgTitle}>
                                <div
                                    className={styles.title}
                                    style={{ paddingRight: notification.dialogType !== 2 ? 60 : 0 }}
                                >
                                    <div dangerouslySetInnerHTML={{ __html: notification.title }} />
                                    {notification.wmPoiName ? (
                                        <span className={styles.poiName}>
                                            {notification.wmPoiName}
                                        </span>
                                    ) : null}
                                </div>
                                {notification.dialogType !== 2 ? (
                                    <span
                                        className={styles.closeMsg}
                                        onClick={() => handleCloseNotification(notification)}
                                    >
                                        +
                                    </span>
                                ) : null}
                            </div>
                            <div
                                className={styles.msgContent}
                                dangerouslySetInnerHTML={{ __html: notification.content }}
                            />
                            <div className={styles.msgOpe}>
                                {notification.dialogType === 2 ? (
                                    renderCustomizeMessageOpe(notification)
                                ) : (
                                    <Button
                                        size="mini"
                                        onClick={() => handleReadNotification(notification)}
                                    >
                                        {getButtonText(notification)}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {foldNotificationCount > 0 ? (
                    <div className={styles.msgFoldTips}>
                        <div>
                            <span className={styles.foldMsgCount}>{foldNotificationCount}</span>{' '}
                            条消息已折叠，上⾯的消息处理后，折叠消息会⾃动展开
                        </div>
                        <span onClick={clearNotification} className={styles.clearAll}>
                            全部清除
                        </span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        shouldShowNotification: !state.shellCommon.customizedShowArr.includes(
            CustomizedShowArrType.PUSH_MESSAGE,
        ),
        notifications: state.pushNotification.strongNotificationList.slice(0, 1),
        foldNotificationCount: Math.max(
            state.pushNotification.strongNotificationList.length - 1,
            0,
        ),
    };
};

const mapDistachToProps = (dispatch: Dispatch) => {
    return {
        clearNotification: () => dispatch({ type: 'pushNotification/clearStrongNotification' }),
        closeNotification: (notification: MessageNotification) =>
            dispatch({
                type: 'pushNotification/closeStrongNotification',
                payload: { wmEPoiMsgId: notification.wmEPoiMsgId },
            }),
    };
};

export default connect(mapStateToProps, mapDistachToProps)(StrongNotificationBox);
